/**
 * useInspectionEdit — PM-side mutation that PATCHes /api/admin/inspections/{id}.
 *
 * On success, invalidates 3 query keys to drive map recolor + per-asset
 * inspection list refresh:
 *   - ASSETS_GEOJSON_KEY            -> map dimension chips recolor (D-87 ≤200ms)
 *   - INSPECTION_KEY(inspectionId)  -> single-inspection cache (placeholder
 *                                     for Phase 6 history timeline)
 *   - ASSET_INSPECTIONS_KEY(asset)  -> per-asset list refreshes the pm_edit_count
 *                                     badge in AssetSidePanel
 *
 * Plan 04 (Wave 2) does NOT modify web/src/api/assets.ts. The transport-layer
 * (SSE channel + refetchInterval policy) is owned end-to-end by Plan 05 per
 * RESEARCH.md Q1 RESOLVED. This hook only INVALIDATES the existing
 * useAssetGeoJson cache; the cache config itself is Plan 05's concern.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPatch, ApiError } from "../../api/client";
import { ASSETS_GEOJSON_KEY } from "../../api/assets";
import {
  ASSET_INSPECTIONS_KEY,
  INSPECTION_KEY,
  type InspectionEditPayload,
  type InspectionUpdatedResponse,
} from "../../api/inspections";

export function useInspectionEdit(inspectionId: number, assetId: number) {
  const qc = useQueryClient();
  return useMutation<InspectionUpdatedResponse, ApiError, InspectionEditPayload>({
    mutationFn: (payload) =>
      apiPatch<InspectionUpdatedResponse>(
        `/api/admin/inspections/${inspectionId}`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ASSETS_GEOJSON_KEY });
      qc.invalidateQueries({ queryKey: INSPECTION_KEY(inspectionId) });
      qc.invalidateQueries({ queryKey: ASSET_INSPECTIONS_KEY(assetId) });
    },
  });
}
